import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pwc201f1Api, FC100, DetailRequest } from '../../../services/pwc201f1Api';

export type Mode = 'browse' | 'add' | 'edit';

const emptyDetail = (): DetailRequest => ({
  fc101_11: '', fc101_12: '', fc101_13: 0, fc101_14: 3, fc101_15: 0,
  fc101_16: 0, fc101_17: 0, fc101_18: '', fc101_19: 0,
  fc101_21: '', fc101_211: '', fc101_22: '', fc101_221: '',
  fc101_23: '', fc101_231: '', fc101_24: '',
});

export function usePWC201F1Form() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FC100[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Header fields
  const [quoteDate, setQuoteDate] = useState('');
  const [validDate, setValidDate] = useState('');
  const [custCode, setCustCode] = useState('');
  const [salesRep, setSalesRep] = useState('');
  const [currency, setCurrency] = useState('NTD');
  const [exchRate, setExchRate] = useState(1);
  const [custAddr, setCustAddr] = useState('');
  const [yourRef, setYourRef] = useState('');
  const [remark, setRemark] = useState('');
  const [remarkCode, setRemarkCode] = useState('');
  const [deptCode, setDeptCode] = useState('');

  // Detail lines
  const [details, setDetails] = useState<DetailRequest[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const formatDate = (d: string | null) => {
    if (!d) return '';
    return d.substring(0, 10);
  };

  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await pwc201f1Api.list(1, 1000, keyword);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setQuoteDate(formatDate(current.fc100_11));
      setValidDate(formatDate(current.fc100_14));
      setCustCode(current.fc100_12 || '');
      setSalesRep(current.fc100_13 || '');
      setCurrency(current.fc100_15 || 'NTD');
      setExchRate(current.fc100_16 || 1);
      setCustAddr(current.fc100_18 || '');
      setYourRef(current.fc100_19 || '');
      setRemark(current.fc100_22 || '');
      setRemarkCode(current.fc100_23 || '');
      setDeptCode(current.fc100_24 || '');
      setDetails((current.details || []).map(d => ({
        fc101_11: d.fc101_11, fc101_12: d.fc101_12,
        fc101_13: d.fc101_13, fc101_14: d.fc101_14, fc101_15: d.fc101_15,
        fc101_16: d.fc101_16, fc101_17: d.fc101_17, fc101_18: d.fc101_18,
        fc101_19: d.fc101_19, fc101_21: d.fc101_21, fc101_211: d.fc101_211,
        fc101_22: d.fc101_22, fc101_221: d.fc101_221,
        fc101_23: d.fc101_23, fc101_231: d.fc101_231, fc101_24: d.fc101_24,
      })));
    }
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const today = () => new Date().toISOString().substring(0, 10);

  const startAdd = () => {
    setMode('add');
    setQuoteDate(today());
    setValidDate(today());
    setCustCode(''); setSalesRep(''); setCurrency('NTD'); setExchRate(1);
    setCustAddr(''); setYourRef(''); setRemark(''); setRemarkCode(''); setDeptCode('');
    setDetails([emptyDetail()]);
  };

  const startEdit = () => {
    if (!current) return;
    setMode('edit');
  };

  const cancel = () => {
    setMode('browse');
    if (current) {
      setQuoteDate(formatDate(current.fc100_11));
      setValidDate(formatDate(current.fc100_14));
      setCustCode(current.fc100_12 || '');
      setSalesRep(current.fc100_13 || '');
      setCurrency(current.fc100_15 || 'NTD');
      setExchRate(current.fc100_16 || 1);
      setCustAddr(current.fc100_18 || '');
      setYourRef(current.fc100_19 || '');
      setRemark(current.fc100_22 || '');
      setRemarkCode(current.fc100_23 || '');
      setDeptCode(current.fc100_24 || '');
    }
  };

  const buildPayload = () => ({
    fc100_11: quoteDate,
    fc100_12: custCode,
    fc100_13: salesRep,
    fc100_14: validDate,
    fc100_15: currency,
    fc100_16: exchRate,
    fc100_18: custAddr,
    fc100_19: yourRef,
    fc100_22: remark,
    fc100_23: remarkCode,
    fc100_24: deptCode,
    details,
  });

  const save = async () => {
    try {
      let saved: FC100;
      if (mode === 'add') {
        saved = await pwc201f1Api.create(buildPayload());
        message.success('新增成功');
      } else {
        saved = await pwc201f1Api.update(current!.fc100_01, buildPayload());
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.fc100_01 === saved.fc100_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await pwc201f1Api.delete(current.fc100_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const addDetailLine = () => {
    if (details.length >= 99) { message.warning('最多99項'); return; }
    setDetails([...details, emptyDetail()]);
  };

  const removeDetailLine = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };

  const updateDetail = (idx: number, field: keyof DetailRequest, value: any) => {
    const updated = [...details];
    (updated[idx] as any)[field] = value;
    setDetails(updated);
  };

  const quickSearch = async (keyword: string) => {
    if (isEditing) return;
    const data = await loadRecords(keyword);
    if (data.length > 0) setCurrentIdx(0);
    else setCurrentIdx(-1);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    quoteDate, setQuoteDate, validDate, setValidDate,
    custCode, setCustCode, salesRep, setSalesRep,
    currency, setCurrency, exchRate, setExchRate,
    custAddr, setCustAddr, yourRef, setYourRef,
    remark, setRemark, remarkCode, setRemarkCode,
    deptCode, setDeptCode,
    details, addDetailLine, removeDetailLine, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  };
}
